import type { Page } from "@/schemas/firestore/page.schema";
import type { WordPressVersion } from "@/schemas/firestore/wordpressVersion.schema";

export type PageWorkflowStatus =
  | "No Draft"
  | "Loading"
  | "Conflict"
  | "Error"
  | "Publish Failed"
  | "Publishing"
  | "Awaiting Approval"
  | "Published"
  | "Approved"
  | "Rejected"
  | "Draft In Progress";

export function getPageWorkflowStatus(
  page: Page,
  versions: WordPressVersion[] | undefined,
  is_loading_versions: boolean,
): PageWorkflowStatus {
  if (page.source !== "wordpress") {
    return "No Draft";
  }

  if (is_loading_versions) {
    return "Loading";
  }

  const latestDraft = versions?.find((version) => version.origin !== "sync");
  if (!latestDraft) {
    return "No Draft";
  }

  if (
    latestDraft.origin === "conflict" ||
    latestDraft.review_status === "conflict"
  ) {
    return "Conflict";
  }

  if (latestDraft.generation_error) {
    return "Error";
  }

  if (latestDraft.publish_error || latestDraft.publish_status === "failed") {
    return "Publish Failed";
  }

  if (
    latestDraft.publish_status === "queued" ||
    latestDraft.publish_status === "publishing"
  ) {
    return "Publishing";
  }

  if (latestDraft.review_status === "ready_for_review") {
    return "Awaiting Approval";
  }

  if (
    latestDraft.publish_status === "published" &&
    latestDraft.review_status === "approved"
  ) {
    return "Published";
  }

  if (latestDraft.review_status === "approved") {
    return "Approved";
  }

  if (latestDraft.review_status === "rejected") {
    return "Rejected";
  }

  return "Draft In Progress";
}

export function isDraftInProgress(
  page: Page,
  versions: WordPressVersion[] | undefined,
  is_loading_versions: boolean,
): boolean {
  return (
    getPageWorkflowStatus(page, versions, is_loading_versions) ===
    "Draft In Progress"
  );
}
